import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { AppleEmoji } from '../components/Emoji';
import {
  IconFolder, IconImage, IconVideo, IconEdit, IconTrash,
  IconPlus, IconUpload, IconSearch, IconCheck, IconDownload,
  IconX, IconLayers, IconSparkles
} from '../components/Icons';

interface Brand {
  id: string;
  name: string;
  logo?: string;
}

interface Folder {
  id: string;
  name: string;
  parentId?: string | null;
  _count: { files: number; children: number };
  createdBy?: { firstName: string; lastName: string };
}

interface DriveFile {
  id: string;
  name: string;
  fileUrl: string;
  fileType?: string;
  fileSize?: number;
  folderId?: string | null;
  createdAt: string;
  createdBy?: { id: string; firstName: string; lastName: string; avatar?: string };
}

interface UploadTask {
  id: string;
  name: string;
  folderPath?: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
}

/* =========================================================================
   COMPONENTE: CARPETA ANIMADA CON APERTURA EN 3D AL PASAR EL MOUSE (HOVER)
   ========================================================================= */
interface AnimatedGlassFolderProps {
  folder: Folder;
  isDragOver: boolean;
  isAdmin: boolean;
  onClick: () => void;
  onRename: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

const AnimatedGlassFolder: React.FC<AnimatedGlassFolderProps> = ({
  folder,
  isDragOver,
  isAdmin,
  onClick,
  onRename,
  onDelete,
  onDragOver,
  onDragLeave,
  onDrop
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        position: 'relative',
        background: isDragOver
          ? 'rgba(0, 112, 243, 0.12)'
          : isHovered
            ? 'rgba(255, 255, 255, 0.92)'
            : 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: '18px',
        border: isDragOver
          ? '2px dashed #0070f3'
          : isHovered
            ? '1px solid rgba(0, 112, 243, 0.45)'
            : '1px solid rgba(255, 255, 255, 0.8)',
        padding: '18px 16px 14px',
        cursor: 'pointer',
        boxShadow: isHovered
          ? '0 14px 30px -6px rgba(15, 42, 74, 0.12), inset 0 1.5px 2px rgba(255, 255, 255, 1)'
          : '0 4px 16px -2px rgba(15, 42, 74, 0.05), inset 0 1.5px 2px rgba(255, 255, 255, 0.85)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        perspective: '600px',
        userSelect: 'none'
      }}
    >
      {/* Botones de acción flotantes de la carpeta */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          display: 'flex',
          gap: '5px',
          zIndex: 20,
          opacity: isHovered ? 1 : 0.4,
          transition: 'opacity 0.2s ease'
        }}
      >
        <button
          type="button"
          onClick={onRename}
          title="Renombrar carpeta"
          style={{
            width: '26px',
            height: '26px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.9)',
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(8px)',
            color: '#0070f3',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
          }}
        >
          <IconEdit size={12} color="#0070f3" />
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={onDelete}
            title="Eliminar carpeta y contenidos"
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.9)',
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(8px)',
              color: '#ef4444',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
            }}
          >
            <IconTrash size={12} color="#ef4444" />
          </button>
        )}
      </div>

      {/* REPRESENTACIÓN 3D DE LA CARPETA QUE SE ABRE AL PASAR EL MOUSE */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px' }}>
        <div
          style={{
            position: 'relative',
            width: '46px',
            height: '38px',
            perspective: '400px',
            flexShrink: 0
          }}
        >
          {/* Capa Trasera de la Carpeta */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, #0070f3 0%, #0051b3 100%)',
              borderRadius: '6px 10px 8px 8px',
              boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08)',
              overflow: 'hidden'
            }}
          >
            {/* Pestaña superior izquierda */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '18px',
                height: '7px',
                background: '#0051b3',
                borderRadius: '4px 4px 0 0'
              }}
            />
          </div>

          {/* Documentos/Hojas interiores que asoman cuando se abre la carpeta */}
          <div
            style={{
              position: 'absolute',
              top: '4px',
              left: '8px',
              width: '26px',
              height: '22px',
              background: '#ffffff',
              borderRadius: '3px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
              transformOrigin: 'bottom center',
              transform: isHovered
                ? 'translateY(-10px) rotate(-5deg) scale(1.05)'
                : 'translateY(0) rotate(0deg) scale(0.9)',
              opacity: isHovered ? 1 : 0.6,
              transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '3px'
            }}
          >
            <div style={{ width: '80%', height: '2px', background: '#cbd5e1', borderRadius: '1px', marginBottom: '2px' }} />
            <div style={{ width: '55%', height: '2px', background: '#94a3b8', borderRadius: '1px' }} />
          </div>

          <div
            style={{
              position: 'absolute',
              top: '5px',
              left: '12px',
              width: '24px',
              height: '20px',
              background: '#f8fafc',
              borderRadius: '3px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              transformOrigin: 'bottom center',
              transform: isHovered
                ? 'translateY(-8px) rotate(4deg) scale(1.02)'
                : 'translateY(0) rotate(0deg) scale(0.85)',
              opacity: isHovered ? 0.95 : 0.4,
              transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
              zIndex: 3
            }}
          />

          {/* Tapa Delantera de la Carpeta (Flap que rota en 3D abriéndose) */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '30px',
              background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.95) 0%, rgba(0, 112, 243, 0.95) 100%)',
              backdropFilter: 'blur(6px)',
              borderRadius: '4px 4px 8px 8px',
              boxShadow: '0 -1px 4px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,0.6)',
              transformOrigin: 'bottom center',
              transform: isHovered
                ? 'rotateX(-42deg) skewX(-2deg) translateY(2px)'
                : 'rotateX(0deg) skewX(0deg) translateY(0)',
              transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s ease',
              filter: isHovered ? 'brightness(1.08)' : 'none',
              zIndex: 4
            }}
          >
            {/* Brillo especular en la tapa */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1.5px',
                background: 'rgba(255, 255, 255, 0.7)'
              }}
            />
          </div>
        </div>

        {/* Nombre y datos de la carpeta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '13px',
              fontWeight: 800,
              color: '#0f2a4a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: '1.3'
            }}
          >
            {folder.name}
          </div>
          <div
            style={{
              fontSize: '11px',
              color: isHovered ? '#0070f3' : '#64748b',
              fontWeight: 600,
              marginTop: '3px',
              transition: 'color 0.2s ease'
            }}
          >
            {folder._count.files} archivo(s) • {folder._count.children} subcarpeta(s)
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================================================================
   COMPONENTE PRINCIPAL: PÁGINA DE DRIVE CON LIQUID GLASS Y POPUPS GLASS
   ========================================================================= */
export const DrivePage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = ['ADMIN', 'CUENTAS', 'CONTENIDO', 'DISENO'].includes(user?.role || '');

  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandId, setBrandId] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [path, setPath] = useState<{ id: string | null; name: string }[]>([{ id: null, name: 'Mi Drive' }]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);

  // Vistas y filtros
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Previsualización y arrastre
  const [preview, setPreview] = useState<DriveFile | null>(null);
  const [isDraggingOverScreen, setIsDraggingOverScreen] = useState(false);
  const [draggingFile, setDraggingFile] = useState<DriveFile | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);

  // Widget Flotante de Subida estilo Google Drive
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const [showUploadWidget, setShowUploadWidget] = useState(false);
  const [isUploadMinimized, setIsUploadMinimized] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const newMenuRef = useRef<HTMLDivElement>(null);
  const brandDropdownRef = useRef<HTMLDivElement>(null);

  const currentFolderId = path[path.length - 1].id;
  const currentBrand = brands.find(b => b.id === brandId);

  // Cargar contenido
  const load = async (bId?: string, fId?: string | null) => {
    const activeBId = bId ?? brandId;
    const activeFId = fId !== undefined ? fId : currentFolderId;
    if (!activeBId) return;

    setLoading(true);
    try {
      const res = await api.get('/drive', {
        params: { brandId: activeBId, folderId: activeFId || undefined }
      });
      setFolders(Array.isArray(res.data?.folders) ? res.data.folders : []);
      setFiles(Array.isArray(res.data?.files) ? res.data.files : []);
    } catch {
      toast.error('Error al cargar contenido de Drive');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetFolderId = params.get('folderId');
    const targetBrandId = params.get('brandId');

    api.get('/brands').then(async r => {
      const bs = Array.isArray(r.data) ? r.data : [];
      setBrands(bs);

      if (targetFolderId) {
        try {
          const folRes = await api.get('/drive', { params: { folderId: targetFolderId } });
          const targetFolder = folRes.data?.folder;
          const assignedBrandId = targetFolder?.brandId || targetBrandId || (bs.length > 0 ? bs[0].id : '');

          if (assignedBrandId) setBrandId(assignedBrandId);
          if (targetFolder) {
            setPath([{ id: null, name: 'Mi Drive' }, { id: targetFolder.id, name: targetFolder.name }]);
          }
          load(assignedBrandId, targetFolderId);
          return;
        } catch {}
      }

      const activeBId = targetBrandId || (bs.length > 0 ? bs[0].id : '');
      if (activeBId) {
        setBrandId(activeBId);
        load(activeBId, null);
      }
    });
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target as Node)) {
        setShowNewMenu(false);
      }
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(e.target as Node)) {
        setShowBrandDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openFolder = (folder: Folder) => {
    setPath(p => [...p, { id: folder.id, name: folder.name }]);
    load(brandId, folder.id);
  };

  const navTo = (idx: number) => {
    const newPath = path.slice(0, idx + 1);
    setPath(newPath);
    load(brandId, newPath[newPath.length - 1].id);
  };

  const handleBrandChange = (id: string) => {
    setBrandId(id);
    setShowBrandDropdown(false);
    setPath([{ id: null, name: 'Mi Drive' }]);
    load(id, null);
  };

  // Crear Carpeta
  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim() || !brandId) return;

    setCreatingFolder(true);
    try {
      await api.post('/drive/folders', {
        name: newFolderName.trim(),
        brandId,
        parentId: currentFolderId || undefined
      });
      toast.success(`Carpeta "${newFolderName.trim()}" creada`);
      setNewFolderName('');
      setShowNewFolderModal(false);
      load(brandId, currentFolderId);
    } catch {
      toast.error('Error al crear carpeta');
    } finally {
      setCreatingFolder(false);
    }
  };

  // Renombrar Carpeta
  const handleRenameFolder = async (folder: Folder) => {
    const newName = window.prompt('Nuevo nombre para la carpeta:', folder.name);
    if (!newName || !newName.trim() || newName.trim() === folder.name) return;

    try {
      await api.patch(`/drive/folders/${folder.id}`, { name: newName.trim() });
      toast.success('Carpeta renombrada');
      load(brandId, currentFolderId);
    } catch {
      toast.error('Error al renombrar');
    }
  };

  // Eliminar Carpeta (Cascada Total)
  const handleDeleteFolder = async (folder: Folder) => {
    if (!window.confirm(`¿Estás seguro de eliminar la carpeta "${folder.name}" y todos sus archivos? Esta acción no se puede deshacer.`)) return;

    try {
      await api.delete(`/drive/folders/${folder.id}`);
      toast.success('Carpeta y contenidos eliminados');
      load(brandId, currentFolderId);
    } catch {
      toast.error('Error al eliminar carpeta');
    }
  };

  // Renombrar Archivo
  const handleRenameFile = async (file: DriveFile) => {
    const newName = window.prompt('Nuevo nombre para el archivo:', file.name);
    if (!newName || !newName.trim() || newName.trim() === file.name) return;

    try {
      await api.patch(`/drive/files/${file.id}`, { name: newName.trim() });
      toast.success('Archivo renombrado');
      if (preview?.id === file.id) setPreview({ ...preview, name: newName.trim() });
      load(brandId, currentFolderId);
    } catch {
      toast.error('Error al renombrar archivo');
    }
  };

  // Eliminar Archivo
  const handleDeleteFile = async (fileId: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este archivo?')) return;
    try {
      await api.delete(`/drive/files/${fileId}`);
      toast.success('Archivo eliminado');
      if (preview?.id === fileId) setPreview(null);
      load(brandId, currentFolderId);
    } catch {
      toast.error('Error al eliminar archivo');
    }
  };

  // Mover archivo arrastrándolo sobre una carpeta
  const handleMoveFile = async (fileId: string, targetFolderId: string) => {
    try {
      await api.patch(`/drive/files/${fileId}/move`, { folderId: targetFolderId });
      toast.success('Archivo movido');
      load(brandId, currentFolderId);
    } catch {
      toast.error('Error al mover archivo');
    }
  };

  // Subir un archivo individual con reporte en el widget
  const uploadSingleFile = async (file: File, targetFId: string | null, taskId: string) => {
    setUploadTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'uploading' } : t));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('brandId', brandId);
      if (targetFId) fd.append('folderId', targetFId);
      fd.append('name', file.name);

      await api.post('/drive/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed' } : t));
    } catch {
      setUploadTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'error' } : t));
    }
  };

  // Subir Archivos Múltiples desde el botón
  const handleUploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFiles = e.target.files;
    if (!rawFiles || rawFiles.length === 0 || !brandId) return;

    const filesArray = Array.from(rawFiles);
    const newTasks: UploadTask[] = filesArray.map((f, i) => ({
      id: `${Date.now()}-${i}`,
      name: f.name,
      status: 'pending'
    }));

    setUploadTasks(newTasks);
    setShowUploadWidget(true);
    setIsUploadMinimized(false);

    for (let i = 0; i < filesArray.length; i++) {
      await uploadSingleFile(filesArray[i], currentFolderId, newTasks[i].id);
    }

    load(brandId, currentFolderId);
    e.target.value = '';
  };

  // Subir Carpeta Completa desde el botón del explorador
  const handleFolderSelectUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFiles = e.target.files;
    if (!rawFiles || rawFiles.length === 0 || !brandId) return;

    const filesArray = Array.from(rawFiles);
    const newTasks: UploadTask[] = filesArray.map((f, i) => ({
      id: `${Date.now()}-${i}`,
      name: (f as any).webkitRelativePath || f.name,
      status: 'pending'
    }));

    setUploadTasks(newTasks);
    setShowUploadWidget(true);
    setIsUploadMinimized(false);

    try {
      const folderMap = new Map<string, string>(); // relativePath -> createdFolderId

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        const relativePath = (file as any).webkitRelativePath || file.name;
        const pathParts = relativePath.split('/');

        let targetFolder = currentFolderId;

        if (pathParts.length > 1) {
          let currentPathAccum = '';
          let parentFId = currentFolderId;

          for (let p = 0; p < pathParts.length - 1; p++) {
            const segmentName = pathParts[p];
            currentPathAccum = currentPathAccum ? `${currentPathAccum}/${segmentName}` : segmentName;

            if (folderMap.has(currentPathAccum)) {
              parentFId = folderMap.get(currentPathAccum)!;
            } else {
              const res = await api.post('/drive/folders', {
                name: segmentName,
                brandId,
                parentId: parentFId || undefined
              });
              const newFId = res.data?.id;
              folderMap.set(currentPathAccum, newFId);
              parentFId = newFId;
            }
          }
          targetFolder = parentFId;
        }

        await uploadSingleFile(file, targetFolder, newTasks[i].id);
      }
      load(brandId, currentFolderId);
    } catch {
      toast.error('Error al subir carpeta');
    } finally {
      e.target.value = '';
    }
  };

  // Subida por Arrastre (Drag & Drop Externo de Carpetas y Archivos)
  const handleGlobalDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverScreen(false);

    if (!brandId) {
      toast.error('Selecciona una marca primero');
      return;
    }

    const items = e.dataTransfer.items;
    if (!items || items.length === 0) return;

    setShowUploadWidget(true);
    setIsUploadMinimized(false);

    const readEntries = async (dirReader: any): Promise<any[]> => {
      let all: any[] = [];
      let read = async () => new Promise<any[]>(res => dirReader.readEntries(res));
      let res = await read();
      while (res.length > 0) {
        all = all.concat(res);
        res = await read();
      }
      return all;
    };

    const processEntry = async (entry: any, parentFId: string | null) => {
      if (entry.isFile) {
        await new Promise<void>((resolve) => {
          entry.file(async (file: File) => {
            const taskId = `${Date.now()}-${Math.random()}`;
            setUploadTasks(prev => [...prev, { id: taskId, name: file.name, status: 'pending' }]);
            await uploadSingleFile(file, parentFId, taskId);
            resolve();
          });
        });
      } else if (entry.isDirectory) {
        try {
          const res = await api.post('/drive/folders', {
            name: entry.name,
            brandId,
            parentId: parentFId || undefined
          });
          const newFId = res.data?.id;
          const reader = entry.createReader();
          const children = await readEntries(reader);
          for (const child of children) {
            await processEntry(child, newFId);
          }
        } catch {}
      }
    };

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = (item as any).webkitGetAsEntry ? (item as any).webkitGetAsEntry() : null;
        if (entry) {
          await processEntry(entry, currentFolderId);
        } else {
          const file = item.getAsFile();
          if (file) {
            const taskId = `${Date.now()}-${i}`;
            setUploadTasks(prev => [...prev, { id: taskId, name: file.name, status: 'pending' }]);
            await uploadSingleFile(file, currentFolderId, taskId);
          }
        }
      }
    }

    load(brandId, currentFolderId);
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const filteredFolders = folders.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredFiles = files.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const completedUploadsCount = uploadTasks.filter(t => t.status === 'completed').length;
  const isUploadingActive = uploadTasks.some(t => t.status === 'uploading' || t.status === 'pending');

  return (
    <div className="zoik-page-container" style={{ display: 'flex', height: '100vh', maxHeight: '100vh', width: '100vw', maxWidth: '100vw', overflow: 'hidden', background: '#f8fafc', fontFamily: '"Montserrat", sans-serif' }}>
      {/* ESTILOS INLINE PARA ANIMACIONES Y CRISTAL LÍQUIDO */}
      <style>{`
        @keyframes glassDeploy {
          0% {
            opacity: 0;
            transform: scale(0.95) translateY(-8px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes glassModalEnter {
          0% {
            opacity: 0;
            transform: scale(0.92) translateY(16px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        .liquid-glass-card {
          background: rgba(255, 255, 255, 0.72);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.8);
          box-shadow: 0 4px 20px -2px rgba(15, 42, 74, 0.05), inset 0 1.5px 2px rgba(255, 255, 255, 0.9);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .liquid-glass-card:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-3px);
          box-shadow: 0 14px 30px -4px rgba(15, 42, 74, 0.1), inset 0 1.5px 2px rgba(255, 255, 255, 1);
          border-color: rgba(0, 112, 243, 0.3);
        }
        .glass-window-popup {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.85);
          box-shadow: 0 20px 45px -10px rgba(15, 42, 74, 0.18), 0 0 1px 1px rgba(255, 255, 255, 0.9), inset 0 1.5px 2px rgba(255, 255, 255, 0.95);
          animation: glassDeploy 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          transform-origin: top left;
        }
      `}</style>

      <Sidebar />

      <main
        className="zoik-page-main"
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); setIsDraggingOverScreen(true); }}
        onDragLeave={e => { if (e.currentTarget === e.target) setIsDraggingOverScreen(false); }}
        onDrop={handleGlobalDrop}
        style={{ flex: 1, padding: '28px 36px', overflowY: 'auto', position: 'relative' }}
      >
        {/* BARRA SUPERIOR CON CONTROLES LIQUID GLASS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
            {/* Botón "+ Nuevo" Desplegable estilo Ventana Glass */}
            {isAdmin && (
              <div style={{ position: 'relative' }} ref={newMenuRef}>
                <button
                  onClick={() => setShowNewMenu(!showNewMenu)}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    background: 'rgba(0, 112, 243, 0.95)',
                    backdropFilter: 'blur(12px)',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08), inset 0 1px 1.5px rgba(255, 255, 255, 0.35)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
                >
                  <IconPlus size={16} color="#ffffff" />
                  <span>Nuevo</span>
                </button>

                {showNewMenu && (
                  <div
                    className="glass-window-popup"
                    style={{
                      position: 'absolute',
                      top: '48px',
                      left: 0,
                      borderRadius: '18px',
                      width: '210px',
                      zIndex: 100,
                      overflow: 'hidden',
                      padding: '6px'
                    }}
                  >
                    <button
                      onClick={() => { setShowNewFolderModal(true); setShowNewMenu(false); }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: 'none',
                        background: 'transparent',
                        borderRadius: '12px',
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#0f2a4a',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0, 112, 243, 0.08)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(0, 112, 243, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconFolder size={15} color="#0070f3" />
                      </div>
                      <span>Nueva Carpeta</span>
                    </button>

                    <div style={{ height: '1px', background: 'rgba(226, 232, 240, 0.8)', margin: '4px 6px' }} />

                    <button
                      onClick={() => { fileInputRef.current?.click(); setShowNewMenu(false); }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: 'none',
                        background: 'transparent',
                        borderRadius: '12px',
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#0f2a4a',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(16, 185, 129, 0.08)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconUpload size={15} color="#10b981" />
                      </div>
                      <span>Subir Archivos</span>
                    </button>

                    <button
                      onClick={() => { folderInputRef.current?.click(); setShowNewMenu(false); }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: 'none',
                        background: 'transparent',
                        borderRadius: '12px',
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#0f2a4a',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(245, 158, 11, 0.08)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(245, 158, 11, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconFolder size={15} color="#0070f3" />
                      </div>
                      <span>Subir Carpeta</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Selector de Marca estilo Ventana Glass */}
            <div style={{ position: 'relative' }} ref={brandDropdownRef}>
              <button
                type="button"
                onClick={() => setShowBrandDropdown(!showBrandDropdown)}
                style={{
                  padding: '9px 16px',
                  borderRadius: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  background: 'rgba(255, 255, 255, 0.75)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#0f2a4a',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 10px rgba(15, 42, 74, 0.04), inset 0 1px 2px rgba(255, 255, 255, 0.9)',
                  transition: 'all 0.2s ease'
                }}
              >
                <AppleEmoji symbol="🏢" size={16} />
                <span>{currentBrand ? currentBrand.name : 'Seleccionar Marca'}</span>
                <span style={{ fontSize: '10px', color: '#64748b', marginLeft: '4px' }}>▼</span>
              </button>

              {showBrandDropdown && (
                <div
                  className="glass-window-popup"
                  style={{
                    position: 'absolute',
                    top: '46px',
                    left: 0,
                    borderRadius: '16px',
                    width: '230px',
                    zIndex: 100,
                    maxHeight: '260px',
                    overflowY: 'auto',
                    padding: '6px'
                  }}
                >
                  <div style={{ padding: '6px 10px', fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>
                    Marcas Disponibles
                  </div>
                  {brands.map(b => (
                    <button
                      key={b.id}
                      onClick={() => handleBrandChange(b.id)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        background: b.id === brandId ? 'rgba(0, 112, 243, 0.1)' : 'transparent',
                        borderRadius: '10px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: b.id === brandId ? 800 : 600,
                        color: b.id === brandId ? '#0070f3' : '#0f2a4a',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={e => {
                        if (b.id !== brandId) e.currentTarget.style.background = 'rgba(0,0,0,0.03)';
                      }}
                      onMouseLeave={e => {
                        if (b.id !== brandId) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <AppleEmoji symbol="🏢" size={14} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {b.name}
                      </span>
                      {b.id === brandId && <IconCheck size={14} color="#0070f3" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Buscador Liquid Glass */}
          <div style={{ flex: 1, maxWidth: '420px', position: 'relative' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar en esta carpeta..."
              style={{
                width: '100%',
                padding: '10px 14px 10px 38px',
                borderRadius: '14px',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                fontSize: '13px',
                outline: 'none',
                color: '#0f2a4a',
                boxShadow: '0 2px 8px rgba(15, 42, 74, 0.03), inset 0 1px 2px rgba(255, 255, 255, 0.9)',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>
              <IconSearch size={16} />
            </div>
          </div>

          {/* Alternador de Vista Cuadrícula / Lista en Liquid Glass */}
          <div
            style={{
              display: 'flex',
              background: 'rgba(226, 232, 240, 0.6)',
              backdropFilter: 'blur(12px)',
              padding: '3px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.7)'
            }}
          >
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '6px 14px',
                borderRadius: '9px',
                border: 'none',
                background: viewMode === 'grid' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                color: viewMode === 'grid' ? '#0070f3' : '#64748b',
                fontSize: '12px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: viewMode === 'grid' ? '0 2px 8px rgba(15,42,74,0.08)' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              Cuadrícula
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '6px 14px',
                borderRadius: '9px',
                border: 'none',
                background: viewMode === 'list' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                color: viewMode === 'list' ? '#0070f3' : '#64748b',
                fontSize: '12px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: viewMode === 'list' ? '0 2px 8px rgba(15,42,74,0.08)' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              Lista
            </button>
          </div>

          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleUploadFiles} />
          <input ref={folderInputRef} type="file" {...({ webkitdirectory: '', directory: '', multiple: true } as any)} style={{ display: 'none' }} onChange={handleFolderSelectUpload} />
        </div>

        {/* MIGAS DE PAN (BREADCRUMBS) LIQUID GLASS */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px',
            flexWrap: 'wrap',
            padding: '8px 14px',
            borderRadius: '14px',
            background: 'rgba(255, 255, 255, 0.55)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.65)',
            width: 'fit-content'
          }}
        >
          {path.map((p, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span style={{ color: '#94a3b8', fontSize: '13px' }}>›</span>}
              <button
                onClick={() => navTo(idx)}
                style={{
                  background: idx === path.length - 1 ? 'rgba(0, 112, 243, 0.08)' : 'transparent',
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: idx === path.length - 1 ? 900 : 700,
                  color: idx === path.length - 1 ? '#0070f3' : '#64748b',
                  cursor: idx === path.length - 1 ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
              >
                {idx === 0 ? <IconFolder size={14} color="#0070f3" /> : <IconLayers size={13} color="#64748b" />}
                <span>{p.name}</span>
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* SECCIÓN 1: CARPETAS (CON ANIMACIÓN 3D DE APERTURA AL PASAR EL MOUSE) */}
        {filteredFolders.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', marginBottom: '14px', letterSpacing: '0.5px' }}>
              <IconFolder size={13} color="#0070f3" />
              <span>Carpetas ({filteredFolders.length})</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '16px' }}>
              {filteredFolders.map(folder => (
                <AnimatedGlassFolder
                  key={folder.id}
                  folder={folder}
                  isDragOver={dragOverFolder === folder.id}
                  isAdmin={isAdmin}
                  onClick={() => openFolder(folder)}
                  onRename={e => { e.stopPropagation(); handleRenameFolder(folder); }}
                  onDelete={e => { e.stopPropagation(); handleDeleteFolder(folder); }}
                  onDragOver={e => { e.preventDefault(); setDragOverFolder(folder.id); }}
                  onDragLeave={() => setDragOverFolder(null)}
                  onDrop={e => {
                    e.preventDefault();
                    setDragOverFolder(null);
                    if (draggingFile) handleMoveFile(draggingFile.id, folder.id);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* SECCIÓN 2: ARCHIVOS CON LIQUID GLASS */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', marginBottom: '14px', letterSpacing: '0.5px' }}>
            <IconImage size={13} color="#0070f3" />
            <span>Archivos ({filteredFiles.length})</span>
          </div>

          {filteredFiles.length === 0 ? (
            <div
              className="liquid-glass-card"
              style={{
                padding: '48px 24px',
                borderRadius: '20px',
                textAlign: 'center',
                color: '#64748b',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px dashed rgba(203, 213, 225, 0.8)'
              }}
            >
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(0, 112, 243, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                <IconFolder size={28} color="#0070f3" />
              </div>
              <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f2a4a' }}>Esta carpeta está vacía</div>
              <div style={{ fontSize: '12px', marginTop: '4px', maxWidth: '320px', color: '#64748b' }}>
                Arrastra archivos o carpetas aquí desde tu computadora para subirlos en Liquid Glass.
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '16px' }}>
              {filteredFiles.map(file => (
                <div
                  key={file.id}
                  draggable={isAdmin}
                  onDragStart={() => setDraggingFile(file)}
                  onDragEnd={() => setDraggingFile(null)}
                  onClick={() => setPreview(file)}
                  className="liquid-glass-card"
                  style={{
                    borderRadius: '16px',
                    padding: '12px',
                    cursor: 'pointer',
                    position: 'relative',
                    opacity: draggingFile?.id === file.id ? 0.35 : 1
                  }}
                >
                  {/* Botones de acción flotantes del archivo */}
                  <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '4px', zIndex: 10 }}>
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); handleRenameFile(file); }}
                      title="Renombrar"
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.9)',
                        background: 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(8px)',
                        color: '#0070f3',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0
                      }}
                    >
                      <IconEdit size={12} color="#0070f3" />
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); handleDeleteFile(file.id); }}
                        title="Eliminar"
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          border: '1px solid rgba(255, 255, 255, 0.9)',
                          background: 'rgba(255, 255, 255, 0.85)',
                          backdropFilter: 'blur(8px)',
                          color: '#ef4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0
                        }}
                      >
                        <IconTrash size={12} color="#ef4444" />
                      </button>
                    )}
                  </div>

                  {/* Thumbnail / Contenedor visual del archivo */}
                  <div
                    style={{
                      width: '100%',
                      height: '116px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      background: 'rgba(241, 245, 249, 0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '10px',
                      position: 'relative',
                      border: '1px solid rgba(226, 232, 240, 0.6)'
                    }}
                  >
                    {file.fileType?.includes('image') ? (
                      <img src={file.fileUrl} alt={file.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : file.fileType?.includes('video') ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <IconVideo size={34} color="#0070f3" />
                        <span style={{ fontSize: '10px', fontWeight: 800, color: '#0070f3', textTransform: 'uppercase' }}>Video</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <IconImage size={34} color="#64748b" />
                        <span style={{ fontSize: '10px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Documento</span>
                      </div>
                    )}
                  </div>

                  {/* Metadatos del archivo */}
                  <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                    <span>{formatSize(file.fileSize)}</span>
                    <span>{new Date(file.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Vista Lista Liquid Glass */
            <div
              className="liquid-glass-card"
              style={{
                borderRadius: '18px',
                overflow: 'hidden',
                padding: '4px'
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 80px',
                  padding: '12px 18px',
                  background: 'rgba(248, 250, 252, 0.6)',
                  borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#64748b',
                  textTransform: 'uppercase'
                }}
              >
                <span>Nombre</span>
                <span>Subido por</span>
                <span>Fecha</span>
                <span>Tamaño</span>
                <span>Acciones</span>
              </div>
              {filteredFiles.map(file => (
                <div
                  key={file.id}
                  onClick={() => setPreview(file)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 80px',
                    padding: '12px 18px',
                    borderBottom: '1px solid rgba(241, 245, 249, 0.8)',
                    alignItems: 'center',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background 0.15s ease'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 700, color: '#0f2a4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.fileType?.includes('image') ? <IconImage size={16} color="#0070f3" /> : <IconVideo size={16} color="#10b981" />}
                    <span>{file.name}</span>
                  </div>
                  <span style={{ color: '#64748b' }}>{file.createdBy ? `${file.createdBy.firstName} ${file.createdBy.lastName?.[0]}.` : 'Sistema'}</span>
                  <span style={{ color: '#64748b' }}>{new Date(file.createdAt).toLocaleDateString('es-CO')}</span>
                  <span style={{ color: '#64748b' }}>{formatSize(file.fileSize)}</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={e => { e.stopPropagation(); handleRenameFile(file); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#0070f3', padding: '2px' }}><IconEdit size={14} /></button>
                    {isAdmin && <button onClick={e => { e.stopPropagation(); handleDeleteFile(file.id); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}><IconTrash size={14} /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MODAL VENTANA GLASS: CREAR NUEVA CARPETA */}
        {showNewFolderModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 42, 74, 0.45)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px'
            }}
          >
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(28px)',
                WebkitBackdropFilter: 'blur(28px)',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.9)',
                padding: '28px',
                width: '100%',
                maxWidth: '420px',
                boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.25), inset 0 1.5px 2px rgba(255, 255, 255, 1)',
                animation: 'glassModalEnter 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(0, 112, 243, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconFolder size={18} color="#0070f3" />
                </div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 900, color: '#0f2a4a' }}>Nueva Carpeta</h3>
              </div>

              <form onSubmit={handleCreateFolder}>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  placeholder="Ej: Contenido Septiembre 2026..."
                  autoFocus
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(203, 213, 225, 0.8)',
                    background: 'rgba(255, 255, 255, 0.85)',
                    fontSize: '13px',
                    outline: 'none',
                    marginBottom: '20px',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowNewFolderModal(false)}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '12px',
                      border: '1px solid rgba(203, 213, 225, 0.8)',
                      background: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creatingFolder || !newFolderName.trim()}
                    style={{
                      padding: '9px 20px',
                      borderRadius: '12px',
                      border: 'none',
                      background: '#0070f3',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08)'
                    }}
                  >
                    {creatingFolder ? 'Creando...' : 'Crear Carpeta'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL VENTANA GLASS: PREVIEW DE ARCHIVO */}
        {preview && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(5, 13, 26, 0.85)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3000,
              padding: '20px'
            }}
            onClick={() => setPreview(null)}
          >
            {/* Barra de herramientas superior flotante en Glass */}
            <div
              style={{
                position: 'absolute',
                top: '20px',
                right: '24px',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(16px)',
                padding: '6px 10px',
                borderRadius: '14px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <button
                onClick={e => { e.stopPropagation(); handleRenameFile(preview); }}
                style={{
                  padding: '7px 12px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <IconEdit size={13} color="#ffffff" /> Renombrar
              </button>
              <a
                href={preview.fileUrl}
                download={preview.name}
                onClick={e => e.stopPropagation()}
                style={{
                  padding: '7px 12px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '12px',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <IconDownload size={13} color="#ffffff" /> Descargar
              </a>
              {isAdmin && (
                <button
                  onClick={e => { e.stopPropagation(); handleDeleteFile(preview.id); }}
                  style={{
                    padding: '7px 12px',
                    borderRadius: '10px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <IconTrash size={13} color="#ffffff" /> Eliminar
                </button>
              )}
              <button
                onClick={() => setPreview(null)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconX size={14} color="#ffffff" />
              </button>
            </div>

            <div
              style={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '14px',
                animation: 'glassModalEnter 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards'
              }}
              onClick={e => e.stopPropagation()}
            >
              {preview.fileType?.includes('image') ? (
                <img
                  src={preview.fileUrl}
                  alt={preview.name}
                  style={{
                    maxWidth: '85vw',
                    maxHeight: '75vh',
                    borderRadius: '16px',
                    objectFit: 'contain',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
                    border: '1px solid rgba(255,255,255,0.15)'
                  }}
                />
              ) : preview.fileType?.includes('video') ? (
                <video
                  src={preview.fileUrl}
                  controls
                  autoPlay
                  style={{
                    maxWidth: '85vw',
                    maxHeight: '75vh',
                    borderRadius: '16px',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
                    border: '1px solid rgba(255,255,255,0.15)'
                  }}
                />
              ) : (
                <iframe
                  src={preview.fileUrl}
                  title={preview.name}
                  style={{
                    width: '85vw',
                    height: '75vh',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '16px',
                    background: 'white'
                  }}
                />
              )}
              <div
                style={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 800,
                  background: 'rgba(0,0,0,0.4)',
                  padding: '6px 14px',
                  borderRadius: '10px',
                  backdropFilter: 'blur(8px)'
                }}
              >
                {preview.name} ({formatSize(preview.fileSize)})
              </div>
            </div>
          </div>
        )}

        {/* OVERLAY VISUAL DE ARRASTRE LIQUID GLASS */}
        {isDraggingOverScreen && (
          <div
            onDragLeave={() => setIsDraggingOverScreen(false)}
            onDrop={handleGlobalDrop}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 112, 243, 0.82)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              zIndex: 3500,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              border: '3px dashed rgba(255, 255, 255, 0.9)',
              margin: '16px',
              borderRadius: '28px',
              boxShadow: '0 20px 60px rgba(0, 112, 243, 0.4)'
            }}
          >
            <div style={{ width: '80px', height: '80px', borderRadius: '24px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
              <IconFolder size={44} color="#ffffff" />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 900, marginBottom: '8px' }}>
              ¡Suelta tu Carpeta o Archivos aquí!
            </h2>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', margin: 0 }}>
              Se guardarán directamente dentro de: <b>{path[path.length - 1].name}</b>
            </p>
          </div>
        )}

        {/* WIDGET FLOTANTE DE SUBIDAS ESTILO GOOGLE DRIVE (LIQUID GLASS) */}
        {showUploadWidget && uploadTasks.length > 0 && (
          <div
            className="glass-window-popup"
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              width: '360px',
              borderRadius: '20px',
              zIndex: 4000,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              padding: 0
            }}
          >
            {/* Cabecera del Widget estilo Glass */}
            <div
              style={{
                padding: '12px 18px',
                background: 'rgba(15, 42, 74, 0.95)',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isUploadingActive ? (
                  <div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #0070f3', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                ) : (
                  <IconCheck size={16} color="#10b981" />
                )}
                <span>
                  {isUploadingActive
                    ? `Subiendo ${completedUploadsCount} de ${uploadTasks.length}...`
                    : `${completedUploadsCount} carga(s) completada(s)`}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setIsUploadMinimized(!isUploadMinimized)}
                  style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}
                >
                  {isUploadMinimized ? '▲' : '▼'}
                </button>
                <button
                  onClick={() => setShowUploadWidget(false)}
                  style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}
                >
                  <IconX size={14} color="#ffffff" />
                </button>
              </div>
            </div>

            {/* Lista detallada de archivos */}
            {!isUploadMinimized && (
              <div style={{ maxHeight: '220px', overflowY: 'auto', padding: '10px 14px', background: 'rgba(248, 250, 252, 0.85)' }}>
                {uploadTasks.map(task => (
                  <div
                    key={task.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 6px',
                      borderBottom: '1px solid rgba(226, 232, 240, 0.7)',
                      fontSize: '12px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px' }}>
                      <IconImage size={14} color="#64748b" />
                      <span style={{ fontWeight: 600, color: '#0f2a4a' }}>{task.name}</span>
                    </div>
                    <div>
                      {task.status === 'completed' && <IconCheck size={15} color="#10b981" />}
                      {task.status === 'uploading' && (
                        <div style={{ width: '12px', height: '12px', border: '2px solid #cbd5e1', borderTop: '2px solid #0070f3', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                      )}
                      {task.status === 'pending' && <span style={{ color: '#94a3b8', fontSize: '11px' }}>En espera</span>}
                      {task.status === 'error' && <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: 800 }}>Error</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default DrivePage;
